import React, { useState } from 'react';
import {
    Drawer, Input, Button, Typography, Tag, Space, Spin, notification,
    Collapse, Table, Row, Col, Tooltip,
} from 'antd';
import {
    ExperimentOutlined, CheckCircleOutlined, CloseCircleOutlined,
    InfoCircleOutlined, EyeOutlined,
} from '@ant-design/icons';
import { testPlan, testConcept } from '../../../api/payments';
import { BOOKING_FIELDS } from '../../FinanceEngine/constants/booking.constants';
import { OPTIONS as OPERATORS } from '../../FinanceEngine/constants/common.constants';
import { IConceptEval, IConceptEvalCondition } from '../../../interfaces/plan';
import styles from '../common/styles.module.scss';

const { Text } = Typography;
const { Panel } = Collapse;

/* ─── Interfaces ─── */

interface ConceptItem {
  _id: string;
  name: string;
  frequency?: string;
  conditions: any[];
  operation: string;
}

interface VariableItem {
  _id: string;
  name: string;
  keyName?: string;
  value: any;
  planID?: string;
}

interface PlanEvalResult {
  name: string;
  total: number;
  toProcess: boolean;
  _id?: string;
  conceptId?: string;
}

interface PlanEvalDrawerProps {
  visible: boolean;
  onClose: () => void;
  plan: any;
  concepts: ConceptItem[];
  variables: VariableItem[];
  renderFormula: (operation: string) => React.ReactNode;
  conceptTarget?: ConceptItem | null;
}

/* ─── Helpers ─── */

const getFieldLabel = (field: string): string => {
    const found = BOOKING_FIELDS.find((f: any) => f.value === field);
    return found ? found.label : field;
};

const getOperatorLabel = (op: string): string => {
    const found = OPERATORS.find((o: any) => o.value === op);
    return found ? found.label : op;
};

const getOperatorColor = (op: string): string => {
    if (op === 'CONTAINS' || op === 'EQUALS') return '#87d068';
    if (op === 'NOT_CONTAINS' || op === 'NOT_EQUALS') return '#ff7a45';
    if (op.includes('GREATER')) return '#177DDC';
    if (op.includes('LESS')) return '#FAAD14';
    return '#8c8c8c';
};

/* ─── Helpers: resolved formula ─── */

const getNestedField = (obj: any, path: string): any => {
    const parts = path.split('.');
    let current = obj;
    for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
            current = current[part];
        } else {
            return undefined;
        }
    }
    return current;
};

interface ResolvedSegment {
  value: number | null;
  varName: string;
}

const resolveFormulaSegment = (
    segment: string,
    allVariables: VariableItem[],
    bookingData: any,
): ResolvedSegment => {
    const varIdMatch = segment.match(/\{\{variable\.(\w+)\}\}/);
    const bookingFieldMatch = segment.match(/^\{\{booking\.([^}]+)\}\}$/);

    let varName = segment;
    if (varIdMatch) {
        const found = allVariables.find((v) => v._id === varIdMatch[1]);
        varName = found?.name || varIdMatch[1];
    } else if (bookingFieldMatch) {
        varName = getFieldLabel(bookingFieldMatch[1]);
    }

    // Variable reference (possibly with lookups)
    if (varIdMatch) {
        const variable = allVariables.find((v) => v._id === varIdMatch[1]);
        if (!variable) return { value: null, varName };

        let baseValue: any = variable.value;
        if (typeof baseValue === 'string') {
            try { baseValue = JSON.parse(baseValue); } catch { /* keep */ }
        }

        // Resolve lookup keys: ["{{booking.field}}"] or ["staticKey"]
        const lookupRegex = /\["([^"]*)"\]/g;
        const lookupKeys: string[] = [];
        let lm = lookupRegex.exec(segment);
        while (lm !== null) {
            const content = lm[1];
            const bookingRef = content.match(/\{\{booking\.([^}]+)\}\}/);
            if (bookingRef) {
                lookupKeys.push(String(getNestedField(bookingData, bookingRef[1])));
            } else {
                lookupKeys.push(content);
            }
            lm = lookupRegex.exec(segment);
        }

        let result = baseValue;
        for (const key of lookupKeys) {
            if (result && typeof result === 'object' && key in result) {
                result = result[key];
            } else {
                return { value: null, varName };
            }
        }

        const num = Number(result);
        return { value: Number.isNaN(num) ? null : num, varName };
    }

    // Direct booking field
    if (bookingFieldMatch) {
        const val = getNestedField(bookingData, bookingFieldMatch[1]);
        const num = Number(val);
        return { value: Number.isNaN(num) ? null : num, varName };
    }

    // Plain number
    const num = Number(segment.trim());
    return { value: Number.isNaN(num) ? null : num, varName: segment.trim() };
};

/* ─── Component ─── */

const PlanEvalDrawer: React.FC<PlanEvalDrawerProps> = ({
    visible, onClose, plan, concepts, variables, renderFormula, conceptTarget,
}) => {
    const isConceptMode = !!conceptTarget;

    const [bookingId, setBookingId] = useState('');
    const [planLoading, setPlanLoading] = useState(false);
    const [planResults, setPlanResults] = useState<PlanEvalResult[] | null>(null);
    const [conceptLoading, setConceptLoading] = useState<string | null>(null);
    const [conceptResults, setConceptResults] = useState<Record<string, IConceptEval>>({});
    const [expandedConcept, setExpandedConcept] = useState<string | string[]>([]);

    const handleClose = () => {
        setBookingId('');
        setPlanResults(null);
        setConceptResults({});
        setExpandedConcept([]);
        onClose();
    };

    const handleTestPlan = async () => {
        if (!bookingId.trim()) {
            notification.warning({ message: 'Ingresa un Booking ID' });
            return;
        }
        setPlanLoading(true);
        setPlanResults(null);
        setConceptResults({});
        setExpandedConcept([]);
        try {
            const { data } = await testPlan(plan._id, { bookingId: bookingId.trim() } as any);
            const results: PlanEvalResult[] = data.data || [];
            setPlanResults(results);
        } catch {
            notification.error({ message: 'Error al evaluar el plan' });
        } finally {
            setPlanLoading(false);
        }
    };

    const handleTestSingleConcept = async () => {
        if (!bookingId.trim() || !conceptTarget) {
            notification.warning({ message: 'Ingresa un Booking ID' });
            return;
        }
        setConceptLoading(conceptTarget._id);
        try {
            const { data } = await testConcept(
                plan._id,
                conceptTarget._id,
        { bookingId: bookingId.trim() } as any,
            );
            const evalResult: IConceptEval = data.data;
            setConceptResults((prev) => ({
                ...prev,
                [conceptTarget._id]: evalResult,
            }));
        } catch {
            notification.error({ message: 'Error al evaluar el concepto' });
        } finally {
            setConceptLoading(null);
        }
    };

    const handleTestConcept = async (conceptId: string) => {
        if (!bookingId.trim()) return;
        setConceptLoading(conceptId);
        try {
            const { data } = await testConcept(plan._id, conceptId, { bookingId: bookingId.trim() } as any);
            const evalResult: IConceptEval = data.data;
            setConceptResults((prev) => ({ ...prev, [conceptId]: evalResult }));
        } catch {
            notification.error({ message: 'Error al evaluar el concepto' });
        } finally {
            setConceptLoading(null);
        }
    };

    /* ─── Render: Input section ─── */

    const renderInput = () => {
        const handleSubmit = isConceptMode ? handleTestSingleConcept : handleTestPlan;
        const loading = isConceptMode
            ? conceptLoading === conceptTarget?._id
            : planLoading;

        return (
            <div style={{ marginBottom: 24 }}>
                <div className={styles.evalInfoBox}>
                    <InfoCircleOutlined style={{ color: '#177DDC', fontSize: 16, marginTop: 2 }} />
                    <span>
                        {isConceptMode
                            ? `Ingresa un Booking ID para evaluar el concepto "${conceptTarget?.name}" con los datos reales del pedido.`
                            : 'Ingresa un Booking ID para simular cómo se calcularía la liquidación de este plan. Se evaluarán todos los conceptos con los datos reales del pedido.'}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Input
                        placeholder="Ej: 888888888"
                        value={bookingId}
                        onChange={(e) => setBookingId(e.target.value)}
                        onPressEnter={handleSubmit}
                        style={{ flex: 1 }}
                    />
                    <Button
                        type="primary"
                        icon={<ExperimentOutlined />}
                        loading={loading}
                        onClick={handleSubmit}
                    >
                        {isConceptMode ? 'Evaluar Concepto' : 'Evaluar Plan'}
                    </Button>
                </div>
            </div>
        );
    };

    /* ─── Render: Stats ─── */

    const renderStats = () => {
        if (!planResults) return null;

        const total = planResults.reduce((acc, r) => acc + r.total, 0);
        const totalRounded = Math.round(total * 100) / 100;
        const passing = planResults.filter((r) => r.toProcess).length;
        const failing = planResults.filter((r) => !r.toProcess).length;

        return (
            <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                <Col span={8}>
                    <div className={styles.evalTotalCard}>
                        <div className={styles.evalTotalCardLabel}>Total</div>
                        <div className={styles.evalTotalCardValue}>
                            {`$${totalRounded.toFixed(2)}`}
                        </div>
                    </div>
                </Col>
                <Col span={8}>
                    <div className={styles.evalTotalCard}>
                        <div className={styles.evalTotalCardLabel}>Conceptos que aplican</div>
                        <div className={styles.evalTotalCardValue} style={{ color: '#87d068' }}>
                            {passing}
                        </div>
                    </div>
                </Col>
                <Col span={8}>
                    <div className={styles.evalTotalCard}>
                        <div className={styles.evalTotalCardLabel}>Conceptos que no aplican</div>
                        <div className={styles.evalTotalCardValue} style={{ color: '#ff4d4f' }}>
                            {failing}
                        </div>
                    </div>
                </Col>
            </Row>
        );
    };

    /* ─── Render: Condition result table ─── */

    const conditionColumns = [
        {
            title: 'Campo',
            dataIndex: 'field',
            key: 'field',
            render: (field: string) => (
                <Text style={{ color: '#2db7f5', fontSize: 13 }}>{getFieldLabel(field)}</Text>
            ),
        },
        {
            title: 'Operador',
            dataIndex: 'operator',
            key: 'operator',
            render: (op: string) => (
                <Tag style={{
                    margin: 0, color: getOperatorColor(op), borderColor: getOperatorColor(op), background: 'transparent',
                }}
                >
                    {getOperatorLabel(op)}
                </Tag>
            ),
        },
        {
            title: 'Esperado',
            dataIndex: 'value',
            key: 'value',
            render: (val: string) => (
                <Text style={{ color: '#FAAD14', fontSize: 13 }}>{val}</Text>
            ),
        },
        {
            title: 'Del pedido',
            dataIndex: 'fieldValue',
            key: 'fieldValue',
            render: (val: string, record: any) => (
                <Text style={{ fontSize: 13, fontFamily: "'Courier New', monospace" }}>
                    {val ?? record.bookingValue ?? '—'}
                </Text>
            ),
        },
        {
            title: 'Resultado',
            dataIndex: 'result',
            key: 'result',
            width: 90,
            align: 'center' as const,
            render: (result: boolean) => (
                result
                    ? <CheckCircleOutlined className={styles.evalResultPass} style={{ fontSize: 18 }} />
                    : <CloseCircleOutlined className={styles.evalResultFail} style={{ fontSize: 18 }} />
            ),
        },
    ];

    /* ─── Render: Resolved formula ─── */

    const renderResolvedFormula = (concept: ConceptItem, evalData: IConceptEval) => {
        const { operation } = concept;
        if (!operation || !evalData.data) return null;

        // Parse top-level segments and operators from formula
        const topRegex = /(\{\{(?:variable|booking)\.[^}]+\}\}(?:\["[^"]*"\])*|[0-9.]+)/g;
        const segments: string[] = [];
        const operators: string[] = [];
        let lastEnd = 0;
        let match = topRegex.exec(operation);

        while (match !== null) {
            const between = operation.substring(lastEnd, match.index).trim();
            if (between && ['+', '-', '*', '/'].includes(between)) {
                operators.push(between);
            }
            segments.push(match[0]);
            lastEnd = match.index + match[0].length;
            match = topRegex.exec(operation);
        }

        if (segments.length === 0) return null;

        const resolved = segments.map(
            (seg) => resolveFormulaSegment(seg, variables, evalData.data),
        );

        if (resolved.every((r) => r.value === null)) return null;

        return (
            <div>
                <div className={styles.evalSectionTitle}>Fórmula resuelta</div>
                <div style={{
                    background: '#141414',
                    border: '1px solid #303030',
                    borderRadius: 8,
                    padding: 16,
                }}
                >
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        justifyContent: 'center',
                    }}
                    >
                        {resolved.map((r, i) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <React.Fragment key={`seg-${i}`}>
                                {i > 0 && operators[i - 1] && (
                                    <span style={{
                                        fontSize: 20, fontWeight: 700, color: '#fff', padding: '0 4px',
                                    }}
                                    >
                                        {operators[i - 1]}
                                    </span>
                                )}
                                <Tooltip title={r.varName}>
                                    <Tag style={{
                                        margin: 0,
                                        color: r.value !== null ? '#87d068' : '#ff4d4f',
                                        background: r.value !== null
                                            ? 'rgba(135,208,104,0.15)'
                                            : 'rgba(255,77,79,0.15)',
                                        border: `1px solid ${r.value !== null
                                            ? 'rgba(135,208,104,0.3)'
                                            : 'rgba(255,77,79,0.3)'}`,
                                        fontWeight: 700,
                                        fontSize: 16,
                                        padding: '4px 14px',
                                        fontFamily: "'Courier New', monospace",
                                    }}
                                    >
                                        {r.value !== null ? r.value.toFixed(2) : '?'}
                                    </Tag>
                                </Tooltip>
                            </React.Fragment>
                        ))}
                        <span style={{
                            fontSize: 20, fontWeight: 700, color: '#fff', padding: '0 8px',
                        }}
                        >
                            =
                        </span>
                        <Tag style={{
                            margin: 0,
                            fontWeight: 700,
                            fontSize: 18,
                            padding: '4px 16px',
                            fontFamily: "'Courier New', monospace",
                            color: evalData.toProcess ? '#87d068' : '#ff4d4f',
                            background: evalData.toProcess
                                ? 'rgba(135,208,104,0.15)'
                                : 'rgba(255,77,79,0.15)',
                            border: `1px solid ${evalData.toProcess
                                ? 'rgba(135,208,104,0.3)'
                                : 'rgba(255,77,79,0.3)'}`,
                        }}
                        >
                            {evalData.total.toFixed(2)}
                        </Tag>
                    </div>
                </div>
            </div>
        );
    };

    /* ─── Render: Concept detail (expanded) ─── */

    const renderConceptDetail = (concept: ConceptItem) => {
        const evalData = conceptResults[concept._id];
        const isLoading = conceptLoading === concept._id;

        if (!evalData && !isLoading) {
            return (
                <div style={{ textAlign: 'center', padding: 16 }}>
                    <Button
                        icon={<EyeOutlined />}
                        loading={isLoading}
                        onClick={() => handleTestConcept(concept._id)}
                    >
                        Ver detalle
                    </Button>
                    <div style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Ejecuta la evaluación individual para ver condiciones y variables resueltas
                        </Text>
                    </div>
                </div>
            );
        }

        if (isLoading) {
            return (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin size="small" />
                </div>
            );
        }

        return (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {/* Total del concepto (primero) */}
                <div className={styles.evalTotalCard}>
                    <div className={styles.evalTotalCardLabel}>Total del concepto</div>
                    <Tag
                        color={evalData.toProcess ? 'success' : 'error'}
                        style={{ margin: '4px 0', fontSize: 12 }}
                    >
                        {evalData.toProcess ? 'Aplica' : 'No aplica'}
                    </Tag>
                    <div
                        className={styles.evalTotalCardValue}
                        style={{ fontSize: 22, color: evalData.toProcess ? '#87d068' : '#ff4d4f' }}
                    >
                        {`$${evalData.total.toFixed(2)}`}
                    </div>
                </div>

                {/* Fórmula visual */}
                {renderFormula(concept.operation)}

                {/* Fórmula resuelta con valores */}
                {renderResolvedFormula(concept, evalData)}

                {/* Condiciones */}
                {evalData.conditions && evalData.conditions.length > 0 && (
                    <div>
                        <div className={styles.evalSectionTitle}>Condiciones</div>
                        <div className={styles.evalInfoBox} style={{ marginBottom: 8 }}>
                            <InfoCircleOutlined style={{ color: '#177DDC', fontSize: 14, marginTop: 2 }} />
                            <span style={{ fontSize: 12 }}>
                                Las condiciones determinan si este concepto aplica al pedido. Todas deben cumplirse.
                            </span>
                        </div>
                        <div className={styles.tableWrapper}>
                            <Table
                                size="small"
                                dataSource={evalData.conditions.map(
                                    (c: IConceptEvalCondition, i: number) => ({ ...c, key: i }),
                                )}
                                columns={conditionColumns}
                                pagination={false}
                            />
                        </div>
                    </div>
                )}
            </Space>
        );
    };

    /* ─── Render: Concepts list ─── */

    const renderConceptsList = () => {
        if (!planResults) return null;

        // Match plan results to concepts by name or index
        const getResultForConcept = (concept: ConceptItem): PlanEvalResult | undefined => planResults.find((r) => r.name === concept.name);

        return (
            <div>
                <div className={styles.evalSectionTitle}>Conceptos evaluados</div>
                <Collapse
                    accordion
                    activeKey={expandedConcept}
                    onChange={(key) => setExpandedConcept(key)}
                    style={{ background: 'transparent', border: 'none' }}
                >
                    {concepts.map((concept) => {
                        const result = getResultForConcept(concept);
                        const applies = result?.toProcess ?? false;
                        const amount = result?.total ?? 0;

                        return (
                            <Panel
                                key={concept._id}
                                style={{
                                    borderBottom: '1px solid #303030',
                                    borderRadius: 0,
                                    background: 'transparent',
                                }}
                                header={(
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                                    }}
                                    >
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 10, flex: 1,
                                        }}
                                        >
                                            <Text strong style={{ fontSize: 13 }}>{concept.name}</Text>
                                            <Tag
                                                color={applies ? 'success' : 'error'}
                                                style={{ margin: 0 }}
                                            >
                                                {applies ? 'Aplica' : 'No aplica'}
                                            </Tag>
                                        </div>
                                        <Text
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 600,
                                                fontFamily: "'Courier New', monospace",
                                                color: applies ? '#87d068' : '#8c8c8c',
                                                textDecoration: applies ? 'none' : 'line-through',
                                            }}
                                        >
                                            {`$${amount.toFixed(2)}`}
                                        </Text>
                                    </div>
                                )}
                            >
                                {renderConceptDetail(concept)}
                            </Panel>
                        );
                    })}
                </Collapse>
            </div>
        );
    };

    /* ─── Render: Total general ─── */

    const renderTotal = () => {
        if (!planResults) return null;
        const total = planResults.reduce((acc, r) => acc + r.total, 0);
        const totalRounded = Math.round(total * 100) / 100;
        const passing = planResults.filter((r) => r.toProcess).length;
        const failing = planResults.filter((r) => !r.toProcess).length;

        return (
            <div
                className={styles.evalTotalCard}
                style={{
                    marginBottom: 20, padding: 20, border: '1px solid rgba(135, 208, 104, 0.3)',
                }}
            >
                <div className={styles.evalTotalCardLabel}>Total general del plan</div>
                <Space size={6} style={{ margin: '4px 0' }}>
                    <Tag color="success" style={{ margin: 0 }}>
                        {`${passing} aplican`}
                    </Tag>
                    {failing > 0 && (
                        <Tag color="error" style={{ margin: 0 }}>
                            {`${failing} no aplican`}
                        </Tag>
                    )}
                </Space>
                <div className={styles.evalTotalCardValue} style={{ fontSize: 32 }}>
                    {`$${totalRounded.toFixed(2)}`}
                </div>
            </div>
        );
    };

    /* ─── Render: Concept mode result ─── */

    const renderConceptModeResult = () => {
        if (!conceptTarget) return null;
        const evalData = conceptResults[conceptTarget._id];
        const isLoading = conceptLoading === conceptTarget._id;

        if (isLoading) {
            return (
                <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12 }}>
                        <Text type="secondary">Evaluando concepto...</Text>
                    </div>
                </div>
            );
        }

        if (!evalData) return null;

        return (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {/* Total del concepto (primero) */}
                <div className={styles.evalTotalCard}>
                    <div className={styles.evalTotalCardLabel}>Total del concepto</div>
                    <Tag
                        color={evalData.toProcess ? 'success' : 'error'}
                        style={{ margin: '4px 0', fontSize: 12 }}
                    >
                        {evalData.toProcess ? 'Aplica' : 'No aplica'}
                    </Tag>
                    <div
                        className={styles.evalTotalCardValue}
                        style={{
                            color: evalData.toProcess ? '#87d068' : '#ff4d4f',
                        }}
                    >
                        {`$${evalData.total.toFixed(2)}`}
                    </div>
                </div>

                {/* Fórmula visual */}
                {renderFormula(conceptTarget.operation)}

                {/* Fórmula resuelta con valores */}
                {renderResolvedFormula(conceptTarget, evalData)}

                {/* Condiciones */}
                {evalData.conditions && evalData.conditions.length > 0 && (
                    <div>
                        <div className={styles.evalSectionTitle}>Condiciones</div>
                        <div className={styles.evalInfoBox} style={{ marginBottom: 8 }}>
                            <InfoCircleOutlined style={{ color: '#177DDC', fontSize: 14, marginTop: 2 }} />
                            <span style={{ fontSize: 12 }}>
                                Las condiciones determinan si este concepto aplica al pedido. Todas deben cumplirse.
                            </span>
                        </div>
                        <div className={styles.tableWrapper}>
                            <Table
                                size="small"
                                dataSource={evalData.conditions.map(
                                    (c: IConceptEvalCondition, i: number) => ({ ...c, key: i }),
                                )}
                                columns={conditionColumns}
                                pagination={false}
                            />
                        </div>
                    </div>
                )}
            </Space>
        );
    };

    const drawerTitle = isConceptMode
        ? `Evaluar concepto: ${conceptTarget?.name || ''}`
        : `Evaluar: ${plan?.name || ''}`;

    return (
        <Drawer
            title={drawerTitle}
            placement="right"
            width={780}
            visible={visible}
            onClose={handleClose}
            bodyStyle={{ paddingBottom: 24 }}
        >
            {renderInput()}

            {isConceptMode ? (
                renderConceptModeResult()
            ) : (
                <>
                    {planLoading && (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 12 }}>
                                <Text type="secondary">Evaluando plan...</Text>
                            </div>
                        </div>
                    )}

                    {planResults && !planLoading && (
                        <>
                            {renderTotal()}
                            {renderStats()}
                            {renderConceptsList()}
                        </>
                    )}
                </>
            )}
        </Drawer>
    );
};

export { PlanEvalDrawer };
